import store from "store";
import { apiClient, occmClient, forwarderClient, downloadCSVClient } from "./clients";
import { utils as i18nNamespace } from "../../src/i18n/namespace";
import { getLocalizedValue as t } from "../../src/i18n";
import { IS_PRODUCTION_SAAS_UI, IS_DEV_SAAS_UI, IS_SAAS_UI, isNcl } from "./app/utils";


// In non-production environments, there might be certain URLs that we want to bypass the 401 interceptor
// We can maintain a list of such URLs and check against it in the interceptor.
//Ex. if an API only has staging and prod versions - and we are using the staging version in dev, then we might get a 401
const URL_IDENTIFIERS_TO_BYPASS_401_HANDLING = ['api.workloads.netapp.com', 'api.cloudsync.netapp.com']; // only for non-prod envs
const RELOAD_COUNT_SESSION_KEY_401 = '401_reload_count';
const TOKEN_EXPIRY_POLL_INTERVAL_MS = 60_000;

let isProcessing401 = false;
let tokenExpiryPollInterval = null;

// Decodes the JWT on-demand — called at most once per 60s poll tick or on
// visibility change, so the decode cost is negligible.
const isTokenExpired = () => {
    const accessToken = store.getState()?.auth?.accessToken;
    if (!accessToken) return false;
    try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        return typeof payload.exp === 'number' && Date.now() >= payload.exp * 1000;
    } catch {
        return false;
    }
};

const shouldBypassUrlFor401ResponseHandling = (url) => {
    if (IS_PRODUCTION_SAAS_UI || URL_IDENTIFIERS_TO_BYPASS_401_HANDLING.length === 0 || !url) {
        return false;
    }
    return URL_IDENTIFIERS_TO_BYPASS_401_HANDLING.some((bypassUrl) => url.includes(bypassUrl));
};

const renewAccessToken = () => {
    store.dispatch({
        type: "AUTH:RENEW-SSO"
    });
};

const reloadPageOnTokenExpiration = () => {
    if (isProcessing401) return;
    isProcessing401 = true;

    //safety check to prevent infinite reload loops - only reload once per sessionStorage key
    const reloadCount = Number(sessionStorage.getItem(RELOAD_COUNT_SESSION_KEY_401) || 0);
    if (reloadCount >= 1) {
        sessionStorage.removeItem(RELOAD_COUNT_SESSION_KEY_401);
        console.error("Session expired repeatedly, not reloading again.");
        return;
    }
    //show error and reload
    console.error("Session expired, reloading to receive new auth token...");
    store.dispatch({
        type: "NOTIFICATIONS:ERROR",
        payload: {
            message: t("session_expired_err_message", i18nNamespace)
        }
    });
    sessionStorage.setItem(RELOAD_COUNT_SESSION_KEY_401, String(reloadCount + 1));
    setTimeout(() => window.location.reload(), 2000);
};

// Renews silently on SaaS (silent SSO); reloads the page on non-SaaS where
// there is no silent SSO capability.
const renewOrReload = () => {
    if (IS_SAAS_UI) {
        renewAccessToken();
    } else {
        reloadPageOnTokenExpiration();
    }
};

const renewOrReloadIfAccessTokenExpired = () => {
    if (isTokenExpired()) {
        console.error('Caught expiration of access token - attempting to renew...');
        renewOrReload();
    } else if (sessionStorage.getItem(RELOAD_COUNT_SESSION_KEY_401) !== null) {
        // Token is valid — clear any stale reload guard left by a previous expiration
        // cycle so a future genuine expiration is not incorrectly blocked.
        // (Mirrors what the response interceptor does on a successful API call.)
        sessionStorage.removeItem(RELOAD_COUNT_SESSION_KEY_401);
    }
};

const handleApi401Error = (url) => {
    //TODO: remove Console-Dev from here after identifying URLs that need to be bypassed in dev environment
    if (shouldBypassUrlFor401ResponseHandling(url) || IS_DEV_SAAS_UI) {
        console.warn(`Bypassing 401 interceptor for URL: ${url}`);
        return;
    }
    // The server's 401 is authoritative — act unconditionally, bypassing the
    // local isTokenExpired() gate (clock skew / server-side revocation may
    // cause the local JWT to appear valid while the server rejects it).
    renewOrReload();
};

const setupResponseInterceptor = (client) => {
    client.interceptors.response.use(
        (response) => {
            // Only clear the stale 401 guard when no 401 is actively being handled.
            // the page reload is already in progress.
            if (!isProcessing401 && sessionStorage.getItem(RELOAD_COUNT_SESSION_KEY_401) !== null) {
                sessionStorage.removeItem(RELOAD_COUNT_SESSION_KEY_401);
            }
            return response;
        },
        (error) => {
            if (error?.response?.status === 401) {
                const url = error?.response?.config?.url;
                // this is used for mfa initial password check
                if (!url.includes('/oauth/token')){
                    handleApi401Error(error?.response?.config?.url);
                }
            }
            return Promise.reject(error);
        }
    );
};

const initializeResponseInterceptors = () => {
    setupResponseInterceptor(apiClient);
    setupResponseInterceptor(occmClient);
    setupResponseInterceptor(forwarderClient);
    setupResponseInterceptor(downloadCSVClient);
};

const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    renewOrReloadIfAccessTokenExpired();
};

const addDocumentVisibilityListener = () => {
    // Instant detection when the user returns to a hidden/background tab.
    document.addEventListener('visibilitychange', handleVisibilityChange);
};


const initializeProactiveTokenExpirationCheck = () => {
    // Clear any existing interval first to avoid accumulation if this is called
    // more than once (e.g. hot-module reload in development).
    if (tokenExpiryPollInterval !== null) {
        clearInterval(tokenExpiryPollInterval);
    }
    // Safety net for active pages that never trigger a visibilitychange event.
    // Poll every 60s.  This is intentionally coarser than the token lifetime so
    // the overhead is negligible.
    tokenExpiryPollInterval = setInterval(renewOrReloadIfAccessTokenExpired, TOKEN_EXPIRY_POLL_INTERVAL_MS);
};

export const initializeClientInterceptors = () => {
    initializeResponseInterceptors();
    addDocumentVisibilityListener();
    initializeProactiveTokenExpirationCheck();
};
